import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';

import { ActiveService } from '../../../core/providers/active/active.service';
import { DataService } from '../../../core/providers/data/data.service';
import { StateService } from '../../../core/providers/state/state.service';

import { REQUEST_LOGIN_OTP, VERIFY_LOGIN_OTP, AUTHENTICATE_SMS_OTP } from './sign-in.graphql';

@Component({
    selector: 'vsf-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignInComponent implements OnDestroy {
    @Input() navigateToOnSuccess: any[] | undefined;
    @Input() displayRegisterLink = true;

    step: 'phone' | 'verify' = 'phone';
    phoneNumber = '';
    phoneError = '';
    codeDigits = [0, 1, 2, 3, 4, 5];
    codeValues: string[] = ['', '', '', '', '', ''];
    resendTimer = 60;
    loading = false;
    private timerInterval: any;

    constructor(private dataService: DataService,
                private stateService: StateService,
                private activeService: ActiveService,
                private router: Router,
                private changeDetector: ChangeDetectorRef,
                private apollo: Apollo) {}

    ngOnDestroy() {
        this.clearTimer();
    }

    onPhoneInput(event: Event) {
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, '');
        if (value.startsWith('0') || value.startsWith('+')) {
            value = value.substring(1);
        }
        this.phoneNumber = value;
        input.value = value;
        this.phoneError = '';
    }

    isPhoneValid(): boolean {
        return this.phoneNumber.length === 9
            && !this.phoneNumber.startsWith('0')
            && !this.phoneNumber.startsWith('+');
    }

    submitPhone() {
        if (!this.isPhoneValid() || this.loading) {
            this.phoneError = 'Please enter a valid 9-digit phone number';
            return;
        }

        this.loading = true;
        this.phoneError = '';
        this.changeDetector.markForCheck();

        const fullPhone = '963' + this.phoneNumber;

        this.dataService.mutate<any>(REQUEST_LOGIN_OTP, {
            phoneNumber: fullPhone,
        }).subscribe({
            next: ({ requestLoginOtp }) => {
                this.loading = false;
                if (requestLoginOtp.success) {
                    this.step = 'verify';
                    this.startResendTimer();
                } else {
                    this.phoneError = requestLoginOtp.message || 'Failed to send verification code.';
                }
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.phoneError = 'Failed to send verification code. Please try again.';
                this.changeDetector.markForCheck();
            },
        });
    }

    onCodeInput(event: Event, index: number) {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/\D/g, '');
        this.codeValues[index] = value;
        input.value = value;

        if (value && index < 5) {
            const next = document.getElementById('code-' + (index + 1)) as HTMLInputElement;
            if (next) {
                next.focus();
            }
        }
    }

    onCodeKeydown(event: KeyboardEvent, index: number) {
        if (event.key === 'Backspace' && !this.codeValues[index] && index > 0) {
            const prev = document.getElementById('code-' + (index - 1)) as HTMLInputElement;
            if (prev) {
                prev.focus();
                this.codeValues[index - 1] = '';
            }
        }
    }

    onCodePaste(event: ClipboardEvent) {
        event.preventDefault();
        const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '') || '';
        for (let i = 0; i < 6 && i < pasted.length; i++) {
            this.codeValues[i] = pasted[i];
        }
        const nextEmpty = this.codeValues.findIndex(v => !v);
        const focusIdx = nextEmpty === -1 ? 5 : nextEmpty;
        const el = document.getElementById('code-' + focusIdx) as HTMLInputElement;
        if (el) {
            el.focus();
        }
        this.changeDetector.markForCheck();
    }

    isCodeComplete(): boolean {
        return this.codeValues.every(v => v.length === 1);
    }

    submitVerification() {
        if (!this.isCodeComplete() || this.loading) {
            return;
        }

        this.loading = true;
        this.phoneError = '';
        this.changeDetector.markForCheck();

        const otp = this.codeValues.join('');
        const fullPhone = '963' + this.phoneNumber;

        this.dataService.mutate<any>(VERIFY_LOGIN_OTP, {
            phoneNumber: fullPhone,
            otp,
        }).subscribe({
            next: ({ verifyLoginOtp }) => {
                if (!verifyLoginOtp.success) {
                    this.loading = false;
                    this.phoneError = verifyLoginOtp.message || 'Invalid verification code. Please try again.';
                    this.changeDetector.markForCheck();
                    return;
                }

                if (verifyLoginOtp.requiresRegistration) {
                    // New user — redirect to sign-up page
                    this.loading = false;
                    this.router.navigate(['/account', 'sign-up'], {
                        queryParams: { phone: fullPhone },
                    });
                } else {
                    // Existing user — authenticate with smsOtp strategy
                    this.authenticateWithSmsOtp(fullPhone);
                }
            },
            error: () => {
                this.loading = false;
                this.phoneError = 'Verification failed. Please try again.';
                this.changeDetector.markForCheck();
            },
        });
    }

    resendCode() {
        if (this.resendTimer > 0 || this.loading) {
            return;
        }

        this.loading = true;
        this.changeDetector.markForCheck();

        const fullPhone = '963' + this.phoneNumber;

        this.dataService.mutate<any>(REQUEST_LOGIN_OTP, {
            phoneNumber: fullPhone,
        }).subscribe({
            next: ({ requestLoginOtp }) => {
                this.loading = false;
                if (requestLoginOtp.success) {
                    this.startResendTimer();
                    this.codeValues = ['', '', '', '', '', ''];
                } else {
                    this.phoneError = requestLoginOtp.message || 'Failed to resend code.';
                }
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.phoneError = 'Failed to resend code. Please try again.';
                this.changeDetector.markForCheck();
            },
        });
    }

    private authenticateWithSmsOtp(phoneNumber: string) {
        this.dataService.mutate<any>(AUTHENTICATE_SMS_OTP, {
            phoneNumber,
        }).subscribe({
            next: ({ authenticate }) => {
                this.loading = false;
                if (authenticate.__typename === 'CurrentUser') {
                    this.apollo.client.clearStore().then(() => {
                        return this.activeService.refetchActiveOrder();
                    }).then(() => {
                        this.stateService.setState('signedIn', true);
                        this.stateService.setState('activeOrderId', null);
                        const commands = this.navigateToOnSuccess || ['/'];
                        this.router.navigate(commands);
                    });
                } else {
                    this.phoneError = authenticate.message || 'Authentication failed. Please try again.';
                    this.changeDetector.markForCheck();
                }
            },
            error: () => {
                this.loading = false;
                this.phoneError = 'Authentication failed. Please try again.';
                this.changeDetector.markForCheck();
            },
        });
    }

    private startResendTimer() {
        this.clearTimer();
        this.resendTimer = 60;
        this.timerInterval = setInterval(() => {
            this.resendTimer--;
            if (this.resendTimer <= 0) {
                this.clearTimer();
            }
            this.changeDetector.markForCheck();
        }, 1000);
    }

    private clearTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }
}
